import { User } from "oidc-client";
import Service from "../../adapters/xhr/Service";
import { eraseCookie, getCookie } from "../../Utils";
import { UserNotFoundInEvfSystemError, UserNotLogInError } from "../../Constant";

export class AuthorizeService extends Service {
  constructor() {
    super("api");
  }
  _callbacks = [];
  _nextSubscriptionId = 0;
  _user = null;
  _isAuthenticated = false;

  // By default pop ups are disabled because they don't work properly on Edge.
  // If you want to enable pop up authentication simply set this flag to false.
  _popUpDisabled = true;

  async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  async getUser() {
    if (this._user && this._user.profile) {
      return this._user.profile;
    }

    let _user = null;

    try {
      await this.get(`/authenticate/GetCurrentUser`, (status, response) => {
        if (response.isSuccess) {
          _user = new User({
            // access_token: getCookie("jwToken"),
            profile: {
              id: response.data.id,
              email: response.data.email,
              role: response.data.role,
            },
          });
        }
        else if (response.errorMessage === UserNotLogInError) {
          window.location.href = `idp-login/Login?returnUrl=${window.location.href}`;
        }
        else if (response.errorMessage === UserNotFoundInEvfSystemError) {
          window.location.href = "/user-not-found-in-evf";
        }
      });
    } catch (error) {
      console.log(error);

      return null;
      // try {
      //   //refresh token
      //   await this.post(`/authenticate/RefreshToken`, null, (status, data) => {
      //     if (data.jwToken) {
      //       user = new User({
      //         access_token: getCookie("jwToken"),
      //         profile: {
      //           //email: data.data.email,
      //         },
      //       });
      //     }
      //   });
      // } catch (refreshTokenError) {
      //   console.log(refreshTokenError);
      // }
    }

    if (_user) this.updateState(_user);

    return _user && _user.profile;
  }

  // We try to authenticate the user in three different ways:
  // 1) We try to see if we can authenticate the user silently. This happens
  //    when the user is already logged in on the IdP and is done using a hidden iframe
  //    on the client.
  // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
  //    redirect flow.
  async signIn(state) {
    // await this.ensureUserManagerInitialized();
    try {
      const silentUser = await this.userManager.signinSilent(this.createArguments());
      this.updateState(silentUser);
      return this.success(state);
    } catch (silentError) {
      // User might not be authenticated, fallback to popup authentication
      console.log("Silent authentication error: ", silentError);

      try {
        if (this._popUpDisabled) {
          throw new Error("Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it.");
        }

        const popUpUser = await this.userManager.signinPopup(this.createArguments());
        this.updateState(popUpUser);
        return this.success(state);
      } catch (popUpError) {
        if (popUpError.message === "Popup window closed") {
          // The user explicitly cancelled the login action by closing an opened popup.
          return this.error("The user closed the window.");
        } else if (!this._popUpDisabled) {
          console.log("Popup authentication error: ", popUpError);
        }

        // PopUps might be blocked by the user, fallback to redirect
        try {
          await this.userManager.signinRedirect(this.createArguments(state));
          return this.redirect();
        } catch (redirectError) {
          console.log("Redirect authentication error: ", redirectError);
          return this.error(redirectError);
        }
      }
    }
  }

  async signInWithIdentity(username, password, returnUrl) {
    // await this.ensureUserManagerInitialized();
    try {
      let user;
      let error;
      await this.post(`/authenticate`, { username: username, password: password }, (status, data) => {
        if (data.isSuccess) {
          user = new User({
            access_token: data.data.jwToken,
            profile: {
              email: "data.data.",
            },
          });
        } else error = data.errorMessage;
      });
      if (user) {
        this.updateState(user);
        return this.success();
      } else {
        return this.error(error);
      }
    } catch (error) {
      return this.error(error);
    }
  }

  async completeSignIn(url) {
    try {
      // await this.ensureUserManagerInitialized();
      const user = await this.userManager.signinCallback(url);
      this.updateState(user);
      return this.success(user && user.state);
    } catch (error) {
      console.log("There was an error signing in: ", error);
      return this.error("There was an error signing in.");
    }
  }

  // We try to sign out the user in two different ways:
  // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
  //    Pop-Up blocker or the user has disabled PopUps.
  // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
  //    post logout redirect flow.
  async signOut(state) {
    // await this.ensureUserManagerInitialized();
    try {
      if (this._popUpDisabled) {
        throw new Error("Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it.");
      }
      await this.userManager.signoutPopup(this.createArguments());
      this.updateState(undefined);
      return this.success(state);
    } catch (popupSignOutError) {
      console.log("Popup signout error: ", popupSignOutError);
      try {
        await this.userManager.signoutRedirect(this.createArguments(state));
        return this.redirect();
      } catch (redirectSignOutError) {
        console.log("Redirect signout error: ", redirectSignOutError);
        return this.error(redirectSignOutError);
      }
    }
  }

  async logOut() {
    window.location.href = `idp-login/logout`;
  }

  async completeSignOut(url) {
    // await this.ensureUserManagerInitialized();
    try {
      const response = await this.userManager.signoutCallback(url);
      this.updateState(null);
      return this.success(response && response.data);
    } catch (error) {
      console.log(`There was an error trying to log out '${error}'.`);
      return this.error(error);
    }
  }

  updateState(user) {
    this._user = user;
    this._isAuthenticated = !!this._user;
    this.notifySubscribers();
  }

  subscribe(callback) {
    this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
    return this._nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId) {
    const subscriptionIndex = this._callbacks
      .map((element, index) => (element.subscription === subscriptionId ? { found: true, index } : { found: false }))
      .filter((element) => element.found === true);
    if (subscriptionIndex.length !== 1) {
      throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
    }

    this._callbacks.splice(subscriptionIndex[0].index, 1);
  }

  notifySubscribers() {
    for (let i = 0; i < this._callbacks.length; i++) {
      const callback = this._callbacks[i].callback;
      callback();
    }
  }

  createArguments(state) {
    return { useReplaceToNavigate: true, data: state };
  }

  error(message) {
    return { status: AuthenticationResultStatus.Fail, message };
  }

  success(state) {
    return { status: AuthenticationResultStatus.Success, state };
  }

  redirect() {
    return { status: AuthenticationResultStatus.Redirect };
  }

  static get instance() {
    return authService;
  }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
  Redirect: "redirect",
  Success: "success",
  Fail: "fail",
};
